import { useCallback, useState } from "react";

import type { SnackSnsColor, SnackSnsIconsSettingInput, SnackSnsType } from "@/lib/gql/__generated__/graphql";
import { type InputMaybe } from "@/lib/gql/__generated__/graphql";

import type {
	ForArrayType,
	ForAutoGenerator,
	ForNodeType,
} from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DesignSnackLayoutSettingsFormModelOnSetViewModelType } from "../..";
import type { DesignSnackBlocksFormModel } from "../../type";

type Props = {
	viewModel: DesignSnackBlocksFormModel["ViewModel"];
	onSetViewModel: DesignSnackLayoutSettingsFormModelOnSetViewModelType;
};

export type SnackBlocksFormModelSNSMethodsType = {
	isShowModal: boolean;
	activateModal: () => void;
	deactivateModal: () => void;
	onOpenChange: (input: boolean) => void;

	snsSetting: ForAutoGenerator<InputMaybe<SnackSnsIconsSettingInput> | undefined, "">;

	snsColor: ForNodeType<SnackSnsColor>;
	onSetSnackSnsColorToSNSColor: (color: SnackSnsColor) => void;

	snsIcons: ForArrayType<
		{
			snsType: SnackSnsType;
			url: string;
			selected: boolean;
		}[]
	>;
	onSetIsSelectedToSNSIcons: (SNSType: SnackSnsType) => void;
	onSetSNSURLToSnsIcons: (SNSType: SnackSnsType, url: string) => void;
	onSetSNSIcons: (snsIcons: SnackBlocksFormModelSNSMethodsType["snsIcons"]) => void;
};

export const useSnackSettingsFormModelSNSMethods = ({
	viewModel,
	onSetViewModel,
}: Props): SnackBlocksFormModelSNSMethodsType => {
	const { snsSetting } = viewModel;
	const snsColor = snsSetting.snsColor;
	const snsIcons = snsSetting.snsIcons;

	const [isShowModal, setIsShowModal] = useState<boolean>(false);
	const activateModal = useCallback(() => {
		setIsShowModal(true);
	}, [setIsShowModal]);
	const deactivateModal = useCallback(() => {
		setIsShowModal(false);
	}, [setIsShowModal]);

	const onOpenChange = useCallback(
		(input: boolean) => {
			setIsShowModal(input);
		},
		[setIsShowModal],
	);

	const onSetSnackSnsColorToSNSColor = useCallback(
		(color: SnackSnsColor) => {
			onSetViewModel([
				{
					key: "snsSetting",
					value: {
						snsIcons: snsSetting?.snsIcons ?? { isEdited: false, hasError: false, value: [] },
						snsColor: {
							isEdited: true,
							value: color,
						},
					},
				},
			]);
		},
		[onSetViewModel, snsSetting?.snsIcons],
	);

	const onSetIsSelectedToSNSIcons = useCallback(
		(SNSType: SnackSnsType) => {
			onSetViewModel([
				{
					key: "snsSetting",
					value: {
						snsColor: snsSetting.snsColor,
						snsIcons: {
							isEdited: true,
							hasError: false,
							value: snsSetting.snsIcons.value.map((snsIcon) => {
								if (snsIcon.snsType.value === SNSType) {
									return {
										...snsIcon,
										selected: {
											isEdited: true,
											value: !snsIcon.selected.value,
										},
									};
								} else {
									return snsIcon;
								}
							}),
						},
					},
				},
			]);
		},
		[onSetViewModel, snsSetting.snsColor, snsSetting.snsIcons.value],
	);

	const onSetSNSURLToSnsIcons = useCallback(
		(SNSType: SnackSnsType, url: string) => {
			onSetViewModel([
				{
					key: "snsSetting",
					value: {
						snsColor: snsSetting.snsColor,
						snsIcons: {
							isEdited: true,
							hasError: false,
							value: snsSetting.snsIcons.value.map((snsIcon) => {
								if (snsIcon.snsType.value === SNSType) {
									return {
										...snsIcon,
										url: {
											isEdited: true,
											value: url,
										},
									};
								} else {
									return snsIcon;
								}
							}),
						},
					},
				},
			]);
		},
		[onSetViewModel, snsSetting.snsColor, snsSetting.snsIcons.value],
	);

	const onSetSNSIcons = useCallback(
		(snsIcons: SnackBlocksFormModelSNSMethodsType["snsIcons"]) => {
			onSetViewModel([
				{
					key: "snsSetting",
					value: {
						snsColor: snsSetting.snsColor,
						snsIcons,
					},
				},
			]);
		},
		[onSetViewModel, snsSetting.snsColor],
	);

	return {
		isShowModal,
		activateModal,
		deactivateModal,
		onOpenChange,

		snsSetting,

		snsColor,
		onSetSnackSnsColorToSNSColor,

		snsIcons,
		onSetIsSelectedToSNSIcons,
		onSetSNSURLToSnsIcons,
		onSetSNSIcons,
	};
};
